/**
 * Team — dealership user management.
 *
 * Backed by /v1/users:
 *   GET  /v1/users/me                    → dealershipId + the caller's roles
 *   GET  /v1/users/dealership/{id}       → the team (Dealer Principal / Super Admin)
 *   POST /v1/users                       → invite { email, firstName, lastName, roles[] }
 *   PUT  /v1/users/{id}                  → update { firstName, lastName, roles[] }
 *   PUT  /v1/users/{id}/activate|deactivate
 *   DELETE /v1/users/{id}                → super-admin only
 *
 * Invited users are created in Keycloak and land as PENDING_INVITE until they
 * first log in (JIT provisioning flips them to ACTIVE).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import RoleSelector from '../components/RoleSelector';
import { usersService } from '../lib/services';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import {
  ROLE_META, ALL_ROLES, ASSIGNABLE_ROLES, CONSULTANT_ROLES, STATUS_META,
  roleLabel, roleTone, canManageUsers, canDeleteUsers,
} from '../lib/roles';

const fullName = (u) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || '—';

const initialsOf = (u) => {
  const n = [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || '?';
  return n.split(/\s+/).map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?';
};

const fmtDate = (iso) => formatInOrgTz(iso, { day: '2-digit', month: 'short', year: 'numeric' });

export default function Team() {
  const { toast } = useToast();

  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const myRoles = me?.roles || [];
  const canManage = canManageUsers(myRoles);
  const canDelete = canDeleteUsers(myRoles);
  /**
   * Filter options = the nine assignable roles, always — the dealership's
   * actual vocabulary, whether or not every post is filled yet. Retired or
   * out-of-scope roles (legacy Sales Consultant, Auditor…) stay out of the
   * list UNLESS somebody still holds one, in which case it is appended so the
   * people left to migrate remain findable — and it disappears again the day
   * the last holder is moved.
   */
  const filterRoles = useMemo(() => {
    const held = new Set(users.flatMap((u) => u.roles || []));
    const legacyStillHeld = ALL_ROLES.filter((r) => !ASSIGNABLE_ROLES.includes(r) && held.has(r));
    const out = [...ASSIGNABLE_ROLES, ...legacyStillHeld];
    if (roleFilter !== 'All' && !out.includes(roleFilter)) out.push(roleFilter);
    return out;
  }, [users, roleFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const meRow = await usersService.me();
      setMe(meRow);
      const dealershipId = meRow?.dealershipId;
      if (dealershipId == null) {
        // A super-admin with no tenant binding can't use the dealership list.
        setLoadError('Your account isn’t bound to a dealership, so the team list is unavailable here.');
        setUsers([]);
        return;
      }
      const rows = await usersService.listByDealership(dealershipId, { page: 0, size: 200 });
      setUsers(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Could not load the team'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'All' && !(u.roles || []).includes(roleFilter)) return false;
      if (statusFilter !== 'All' && u.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [fullName(u), u.email, (u.roles || []).map(roleLabel).join(' ')]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [users, query, roleFilter, statusFilter]);

  const counts = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.status === 'ACTIVE').length,
    invited: users.filter(u => u.status === 'PENDING_INVITE').length,
  }), [users]);

  // Activate anyone who isn't already ACTIVE (covers INACTIVE re-enable AND a
  // manual PENDING_INVITE → ACTIVE flip); deactivate an ACTIVE user.
  const toggleStatus = async (u) => {
    try {
      if (u.status === 'ACTIVE') {
        await usersService.deactivate(u.id);
        toast(`${fullName(u)} deactivated`, { variant: 'info' });
      } else {
        await usersService.activate(u.id);
        toast(
          u.status === 'PENDING_INVITE'
            ? `${fullName(u)} activated — they still need a login for that email to sign in`
            : `${fullName(u)} reactivated`,
          { variant: 'success' },
        );
      }
      load();
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not change status'), { variant: 'danger' });
    }
  };

  const removeUser = async (u) => {
    const pending = u.status === 'PENDING_INVITE';
    const msg = pending
      ? `Cancel the invitation for ${fullName(u)}?`
      : `Remove ${fullName(u)} permanently? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await usersService.delete(u.id);
      toast(pending ? 'Invitation cancelled' : `${fullName(u)} removed`, { variant: 'success' });
      load();
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not remove the user'), { variant: 'danger' });
    }
  };

  return (
    <>
      <TopBar
        crumbs={['Team']}
        right={
          <>
            <button className="btn small" onClick={load} title="Refresh">↻ Refresh</button>
            {/* Consultant working-hour templates live on the diary settings
                page. Surfaced here so all team/people admin sits in one place
                (moved out of Platform → Settings). */}
            <Link to="/diary-settings" className="btn small" title="Set consultant working hours">
              🕑 Set working hours
            </Link>
            {canManage && (
              <button className="btn small primary" onClick={() => setInviteOpen(true)}>
                + Invite user
              </button>
            )}
          </>
        }
      />
      <div className="app-content">
        {/* Summary */}
        <div className="grid-4" style={{ marginBottom: 14 }}>
          <Tile l="TEAM" v={`${counts.total}`} sub="users in this dealership" c="var(--accent-primary)" />
          <Tile l="ACTIVE" v={`${counts.active}`} sub="can sign in now" c="var(--success)" />
          <Tile l="INVITED" v={`${counts.invited}`} sub="awaiting first login" c="var(--warning)" />
          <Tile
            l="CONSULTANTS"
            v={`${users.filter(u => (u.roles || []).some(r => CONSULTANT_ROLES.includes(r))).length}`}
            sub="bookable in the diary"
            c="var(--info)"
          />
        </div>

        {!canManage && !loading && (
          <div className="card" style={{ marginBottom: 14, borderColor: 'var(--warning)' }}>
            <div className="text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
              You have read-only access here. Inviting and editing users needs the
              Dealer Principal or Super Admin role.
            </div>
          </div>
        )}

        {loadError && (
          <div className="card" style={{ marginBottom: 14, borderColor: 'var(--danger)' }}>
            <div className="text-xs" style={{ color: 'var(--danger)', lineHeight: 1.6 }}>{loadError}</div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or role…"
            aria-label="Search team"
            style={{
              flex: '0 1 300px', minWidth: 180, padding: '7px 10px',
              background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
            }}
          />
          <select className="btn small" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Filter by role">
            <option value="All">All roles</option>
            {filterRoles.map(r => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
          </select>
          <select className="btn small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="All">All statuses</option>
            {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          {(query || roleFilter !== 'All' || statusFilter !== 'All') && (
            <button className="btn small" onClick={() => { setQuery(''); setRoleFilter('All'); setStatusFilter('All'); }}>
              Clear
            </button>
          )}
          <span style={{ marginLeft: 'auto' }} className="mono text-xs text-muted">
            {filtered.length} of {users.length}
          </span>
        </div>

        {/* Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Roles</th><th>Status</th><th>Last login</th><th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6}><div className="mono text-xs text-muted" style={{ padding: 16 }}>Loading…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6}>
                    <div className="empty-state" style={{ padding: 32 }}>
                      <div className="icon">{users.length === 0 ? '👥' : '🔍'}</div>
                      <div className="title">{users.length === 0 ? 'No team members yet' : 'Nothing matches those filters'}</div>
                      <div className="desc">
                        {users.length === 0
                          ? 'Invite your first consultant or manager to get started.'
                          : 'Try a different search, or clear the filters.'}
                      </div>
                      {users.length === 0 ? (
                        <button type="button" className="btn small primary" style={{ marginTop: 8 }} onClick={() => setInviteOpen(true)}>
                          + Invite user
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn small"
                          style={{ marginTop: 8 }}
                          onClick={() => { setQuery(''); setRoleFilter('All'); setStatusFilter('All'); }}
                        >
                          Clear search & filters
                        </button>
                      )}
                    </div>
                  </td></tr>
                ) : filtered.map((u) => {
                  const status = STATUS_META[u.status] || { label: u.status, tone: 'accent' };
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="avatar-pill" style={{ width: 32, height: 32, fontSize: 12 }}>
                            {initialsOf(u)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {fullName(u)}{isSelf && <span className="mono text-xs text-muted"> · you</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="mono text-xs text-muted">{u.email || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(u.roles || []).length === 0
                            ? <span className="mono text-xs text-muted">—</span>
                            : (u.roles || []).map(r => (
                              <span key={r} className={`pill ${roleTone(r)} filled`}>{roleLabel(r)}</span>
                            ))}
                        </div>
                      </td>
                      <td><span className={`pill ${status.tone} filled`}>{status.label}</span></td>
                      <td className="mono text-xs text-muted">{fmtDate(u.lastLoginAt)}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {canManage ? (
                          <>
                            <button className="btn small" onClick={() => setEditUser(u)}>Edit</button>
                            <button
                              className={`btn small ${u.status === 'ACTIVE' ? 'danger' : 'primary'}`}
                              style={{ marginLeft: 6 }}
                              onClick={() => toggleStatus(u)}
                              disabled={isSelf}
                              title={
                                isSelf ? 'You can’t change your own status'
                                  : u.status === 'PENDING_INVITE'
                                    ? 'Mark active now (normally happens automatically on first login)'
                                    : undefined
                              }
                            >
                              {u.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            </button>
                            {canDelete && !isSelf && (
                              <button
                                className="btn small danger"
                                style={{ marginLeft: 6 }}
                                onClick={() => removeUser(u)}
                                title={u.status === 'PENDING_INVITE' ? 'Cancel this invitation' : 'Remove this user'}
                              >
                                {u.status === 'PENDING_INVITE' ? 'Cancel invite' : 'Delete'}
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="mono text-xs text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onInvited={() => { setInviteOpen(false); load(); }}
        />
      )}

      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
    </>
  );
}

/* ── invite modal ── */
function InviteUserModal({ onClose, onInvited }) {
  const { toast } = useToast();
  // No default role. A pre-selected default is invisible once that role stops
  // being rendered, and it silently rides along on every invitation. Submit is
  // already blocked while roles are empty, so this just forces a deliberate choice.
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', roles: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = !busy && emailOk && form.firstName.trim() && form.lastName.trim() && form.roles.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await usersService.create({
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        roles: form.roles,
      });
      toast(`Invited ${form.firstName.trim()} — they’ll get a Keycloak invitation email`, { variant: 'success' });
      onInvited();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the invitation'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="inv-title" onClick={onClose}>
      <form className="modal" style={{ width: 'min(540px, 94vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div>
            <div id="inv-title" style={{ fontSize: 16, fontWeight: 600 }}>Invite user</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Added to your dealership. They confirm via a Keycloak invitation email.
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label htmlFor="inv-email">Email <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input
              id="inv-email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="name@dealership.com.au"
              autoFocus
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="inv-first">First name <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input id="inv-first" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="inv-last">Last name <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input id="inv-last" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="mono text-xs text-muted" style={{ display: 'block', marginBottom: 6, letterSpacing: 0.6 }}>
              ROLES <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <RoleSelector value={form.roles} onChange={(roles) => set('roles', roles)} />
            {form.roles.length === 0 && (
              <div className="mono text-xs" style={{ color: 'var(--warning)', marginTop: 6 }}>
                Pick at least one role.
              </div>
            )}
          </div>
          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Inviting…' : 'Send invitation'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── edit modal ── */
function EditUserModal({ user, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    roles: user.roles || [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // If the user holds SUPER_ADMIN, keep it — we just don't let it be toggled
  // here (it's not in the assignable list).
  const hasSuperAdmin = (user.roles || []).includes('SUPER_ADMIN');
  const canSubmit = !busy && form.firstName.trim() && form.lastName.trim()
    && (form.roles.length > 0 || hasSuperAdmin);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Preserve any SUPER_ADMIN grant that the dealership UI can't set.
      const roles = hasSuperAdmin && !form.roles.includes('SUPER_ADMIN')
        ? ['SUPER_ADMIN', ...form.roles]
        : form.roles;
      await usersService.update(user.id, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        roles,
      });
      toast('User updated', { variant: 'success' });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update the user'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="edt-title" onClick={onClose}>
      <form className="modal" style={{ width: 'min(540px, 94vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div>
            <div id="edt-title" style={{ fontSize: 16, fontWeight: 600 }}>Edit user</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{user.email}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="edt-first">First name</label>
              <input id="edt-first" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} autoFocus />
            </div>
            <div className="form-field">
              <label htmlFor="edt-last">Last name</label>
              <input id="edt-last" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
          </div>
          <div className="mono text-xs text-muted">
            Email is managed by Keycloak and can’t be changed here.
          </div>
          <div>
            <label className="mono text-xs text-muted" style={{ display: 'block', marginBottom: 6, letterSpacing: 0.6 }}>
              ROLES
            </label>
            {hasSuperAdmin && (
              <div className="mono text-xs" style={{ color: 'var(--danger)', marginBottom: 6 }}>
                Holds Super Admin (PMDG). That grant is preserved and managed outside this screen.
              </div>
            )}
            <RoleSelector value={form.roles} onChange={(roles) => set('roles', roles)} />
            {form.roles.length === 0 && !hasSuperAdmin && (
              <div className="mono text-xs" style={{ color: 'var(--warning)', marginTop: 6 }}>
                A user needs at least one role.
              </div>
            )}
          </div>
          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Tile({ l, v, sub, c }) {
  return (
    <div className="card">
      <div className="card-label">{l}</div>
      <div className="metric-value" style={{ fontSize: 26, color: c }}>{v}</div>
      <div className="mono text-xs text-muted">{sub}</div>
    </div>
  );
}
